import { Suspense, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { lazyWithReload as lazy } from '@dfq/shared/lib/lazyWithReload';
import { PageLoader } from '@dfq/shared/components/PageLoader';
import { RequireAuth } from '@dfq/shared/components/RequireAuth';
import { RequireRole } from '@dfq/shared/components/RequireRole';

import { AdminLayout } from '@/views/layout/AdminLayout';

import { adminRoutePaths } from './paths';
import { STAFF_ROLES } from './staffRoles';

/**
 * CON-INFRA-001 — Console app router.
 *
 * Architecture
 * ------------
 * Root routes:
 *   /        → redirect to /admin (the admin dashboard)
 *   /admin/* → RequireRole guard → AdminLayout → lazy-loaded route views
 *   /403     → ForbiddenView (renders outside the admin shell)
 *
 * Every admin view is loaded via `React.lazy()` so `pnpm --filter console
 * build` produces one chunk per view. Wrap each lazy view in a dedicated
 * `<Suspense fallback={<PageLoader />}>` so a slow chunk only suspends the
 * current view, never the entire admin shell.
 *
 * The role guard is currently a stub (see RequireRole). Real enforcement
 * ships with SH-AUTH-005; until then it renders an `<Outlet />` unchanged.
 */

// `@views` alias is configured in both tsconfig.app.json and vite.config.ts,
// so these lazy imports resolve the same way in TS and the Vite build.
const DashboardView = lazy(() => import('@views/admin/dashboard/DashboardView'));

const ProductsListView = lazy(() => import('@views/admin/products/ProductsListView'));
const ProductCreateView = lazy(() => import('@views/admin/products/ProductCreateView'));
const ProductEditView = lazy(() => import('@views/admin/products/ProductEditView'));

// CON-CAT-001 — Admin taxonomy views.
const TaxonomiesListView = lazy(() => import('@views/admin/taxonomies/TaxonomiesListView'));
const TaxonomyDetailView = lazy(() => import('@views/admin/taxonomies/TaxonomyDetailView'));
const TaxonomyEditView = lazy(() => import('@views/admin/taxonomies/TaxonomyEditView'));
const TaxonomyNodeEditView = lazy(() => import('@views/admin/taxonomies/TaxonomyNodeEditView'));
const TaxonomyImportView = lazy(() => import('@views/admin/taxonomies/TaxonomyImportView'));

const CategoriesListView = lazy(() => import('@views/admin/categories/CategoriesListView'));
const CategoryCreateView = lazy(() => import('@views/admin/categories/CategoryCreateView'));
const CategoryEditView = lazy(() => import('@views/admin/categories/CategoryEditView'));

const MaterialsListView = lazy(() => import('@views/admin/materials/MaterialsListView'));
const MaterialCreateView = lazy(() => import('@views/admin/materials/MaterialCreateView'));
const MaterialEditView = lazy(() => import('@views/admin/materials/MaterialEditView'));

// CON-MAT2-003 — Admin finishes/laminations list + create/edit.
const FinishesListView = lazy(() => import('@views/admin/finishes/FinishesListView'));
const FinishCreateView = lazy(() => import('@views/admin/finishes/FinishCreateView'));
const FinishEditView = lazy(() => import('@views/admin/finishes/FinishEditView'));

// CON-SHAPE-001 — Shape Library list page.
const ShapeListView = lazy(() => import('@views/admin/shapes/ShapeListView'));
// CON-SHAPE-002 — Shape create form page.
const ShapeCreateView = lazy(() => import('@views/admin/shapes/ShapeCreateView'));
const ShapeEditView = lazy(() => import('@views/admin/shapes/ShapeEditView'));

// CON-MAT2-004 — Admin product types list page.
const ProductTypeListView = lazy(() => import('@views/admin/productTypes/ProductTypeListView'));
// CON-MAT2-005 — Admin product type create and edit form views.
const ProductTypeCreateView = lazy(() => import('@views/admin/productTypes/ProductTypeCreateView'));
const ProductTypeEditView = lazy(() => import('@views/admin/productTypes/ProductTypeEditView'));

// CON-VEH-001 — slim Phase 2 admin vehicles list + edit (single page handles
// create at `/new` and edit at `/:id`).
const AdminVehicleListPage = lazy(() => import('@views/admin/vehicles/AdminVehicleListPage'));
const AdminVehicleEditPage = lazy(() => import('@views/admin/vehicles/AdminVehicleEditPage'));
// CON-LAUNCH-001 — real NHTSA vehicle sync admin page.
const AdminVehicleSyncPage = lazy(() => import('@views/admin/vehicles/sync/AdminVehicleSyncPage'));
// CON-LAUNCH-004 — Wikipedia dimensions hydration admin page.
const AdminDimensionsHydrationPage = lazy(
  () => import('@views/admin/vehicles/dimensions/AdminDimensionsHydrationPage'),
);
// CON-LAUNCH-005 — Vehicle dimensions curation queue admin page.
const AdminVehicleCurationPage = lazy(() => import('@views/admin/vehicles/curation/AdminVehicleCurationPage'));
const VehiclePvoImportView = lazy(() => import('@views/admin/vehicles/VehiclePvoImportView'));
const VehicleMappingView = lazy(() => import('@views/admin/vehicles/VehicleMappingView'));

const OrdersListView = lazy(() => import('@views/admin/orders/OrdersListView'));
const OrderDetailView = lazy(() => import('@views/admin/orders/OrderDetailView'));
// CON-WRAP-108 — staff-entered wrap order (phone/trade), keyed card or sent quote.
const StaffWrapOrderCreateView = lazy(
  () => import('@views/admin/orders/StaffWrapOrderCreateView'),
);
// Temporary smoke surface for CON-FULFILLMENT-006 browser proof.
// Remove when CON-ORDERS-* ships the real OrderDetailView.
// CON-SHIPPS-002 — Tracking import screen.
const ImportTrackingView = lazy(() => import('@views/admin/orders/ImportTrackingView'));
const OutstandingBalancesView = lazy(
  () => import('@views/admin/orders/OutstandingBalancesView'),
);

const DisputesListView = lazy(() => import('@views/admin/Disputes/DisputesListView'));
const DisputeDetailView = lazy(() => import('@views/admin/Disputes/DisputeDetailView'));

const QuotesListView = lazy(() => import('@views/admin/quotes/QuotesListView'));
const QuoteDetailView = lazy(() => import('@views/admin/quotes/QuoteDetailView'));

// CON-CDR-001 — Admin custom design requests list. CON-DSVC-101 added the detail page.
const CustomDesignRequestsListView = lazy(
  () => import('@views/admin/customDesignRequests/CustomDesignRequestsListView'),
);
const CustomDesignRequestDetailView = lazy(
  () => import('@views/admin/customDesignRequests/CustomDesignRequestDetailView'),
);

const PrepressQueueView = lazy(() => import('@views/admin/prepress/PrepressQueueView'));
const PrepressArtworkView = lazy(() => import('@views/admin/prepress/PrepressArtworkView'));

// CON-FULFILLMENT-002 / -003 — `/admin/production` mounts the real Production
// Dashboard; `/admin/production/shipping` mounts the real Shipping Station.
const ProductionDashboardView = lazy(() => import('@views/admin/production/ProductionDashboardView'));
const ProductionQueueView = lazy(() => import('@views/admin/production/ProductionQueueView'));
const ShippingStationView = lazy(() => import('@views/admin/production/ShippingStationView'));
// CON-FULFILLMENT-004 — operator-facing Job Detail view
const ProductionJobDetailView = lazy(() => import('@views/admin/production/ProductionJobDetailView'));

// CON-FULFILLMENT-005 — Damage Claims admin views.
const DamageClaimsListView = lazy(() => import('@views/admin/DamageClaims/DamageClaimsListView'));
const DamageClaimDetailView = lazy(() => import('@views/admin/DamageClaims/DamageClaimDetailView'));

const CustomersListView = lazy(() => import('@views/admin/customers/CustomersListView'));
const CustomerDetailView = lazy(() => import('@views/admin/customers/CustomerDetailView'));

const CouponsListView = lazy(() => import('@views/admin/promotions/CouponsListView'));
const AutomaticPromotionsView = lazy(() => import('@views/admin/promotions/AutomaticPromotionsView'));
const SalesView = lazy(() => import('@views/admin/promotions/SalesView'));

// CON-PROMO-002 — Coupon create/edit form views (under marketing/coupons/).
const CouponCreateView = lazy(() => import('@views/marketing/coupons/CouponCreateView'));
const CouponEditView = lazy(() => import('@views/marketing/coupons/CouponEditView'));
// CON-PROMO-004 — Tabbed coupon detail page (Edit + Usage tabs).
const CouponDetailPage = lazy(() => import('@views/marketing/coupons/CouponDetailPage'));

// CON-PROMO-005 — Real automatic promotions list page.
const PromotionListPage = lazy(() => import('@views/marketing/promotions/PromotionListPage'));
// CON-PROMO-006 — Promotion create/edit form (co-located with the list page).
const PromotionFormPage = lazy(() => import('@views/marketing/promotions/PromotionFormPage'));

// CON-SEO-002/003 — SEO redirects management view (real implementation from CON-SEO-002).
const RedirectsListView = lazy(() => import('@views/seo/RedirectsListView'));

const PagesListView = lazy(() => import('@views/admin/cms/PagesListView'));
const PageEditorView = lazy(() => import('@views/admin/cms/PageEditorView'));
// CON-CMS-003 — Real FAQ manager replaces the route placeholder.
const FaqManagerView = lazy(() => import('@views/admin/cms/FaqManagerView'));
const GalleryView = lazy(() => import('@views/admin/cms/GalleryView'));
const TestimonialsManagerView = lazy(() => import('@views/admin/cms/TestimonialsManagerView'));

const SalesReportView = lazy(() => import('@views/admin/reports/SalesReportView'));
const ProductsReportView = lazy(() => import('@views/admin/reports/ProductsReportView'));
const ProductionReportView = lazy(() => import('@views/admin/reports/ProductionReportView'));

const GeneralSettingsView = lazy(() => import('@views/admin/settings/GeneralSettingsView'));
const NavigationSettingsView = lazy(() => import('@views/admin/settings/NavigationSettingsView'));
const QuotesSettingsView = lazy(() => import('@views/admin/settings/QuotesSettingsView'));
const TestOrdersSettingsView = lazy(() => import('@views/admin/settings/TestOrdersSettingsView'));
const HomepageSettingsView = lazy(() => import('@views/admin/settings/HomepageSettingsView'));
const ShippingSettingsView = lazy(() => import('@views/admin/settings/ShippingSettingsView'));
const TaxSettingsView = lazy(() => import('@views/admin/settings/TaxSettingsView'));
const EmailSettingsView = lazy(() => import('@views/admin/settings/EmailSettingsView'));
const UsersSettingsView = lazy(() => import('@views/admin/settings/UsersSettingsView'));
const ApiSettingsView = lazy(() => import('@views/admin/settings/ApiSettingsView'));
const StripeSettingsView = lazy(() => import('@views/admin/settings/StripeSettingsView'));
const AuditLogView = lazy(() => import('@views/admin/settings/AuditLogView'));
const ArtworkSettingsView = lazy(() => import('@views/admin/settings/ArtworkSettingsView'));

const ForbiddenView = lazy(() => import('@views/system/ForbiddenView'));

// Auth — public (pre-session) routes
const LoginView = lazy(() => import('@views/auth/LoginView'));

/**
 * Each lazy view renders inside its own Suspense boundary so a slow chunk
 * only suspends the current view (the admin shell stays mounted with a
 * PageLoader in the content area).
 */
function lazyElement(Component: ComponentType): ReactElement {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}


const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to={adminRoutePaths.dashboard} replace />,
  },
  {
    path: adminRoutePaths.forbidden,
    element: lazyElement(ForbiddenView),
  },
  // Public auth routes — mounted outside `AdminLayout` / `RequireAuth`.
  {
    path: adminRoutePaths.auth.login,
    element: lazyElement(LoginView),
  },
  {
    // CON-AUTH-002 — gate everything under `/admin/*` behind the auth guard.
    // `RequireAuth` returns `<Outlet />` when signed in so the nested
    // `RequireRole` + `AdminLayout` routes mount underneath.
    element: <RequireAuth fallbackPath={adminRoutePaths.auth.login} />,
    children: [
      {
        path: '/admin',
        element: (
          <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
            <AdminLayout />
          </RequireRole>
        ),
        children: [
          { index: true, element: lazyElement(DashboardView) },

          // Products
          { path: 'products', element: lazyElement(ProductsListView) },
          { path: 'products/create', element: lazyElement(ProductCreateView) },
          { path: 'products/:id/edit', element: lazyElement(ProductEditView) },

          // Taxonomies — CON-CAT-001
          { path: 'taxonomies', element: lazyElement(TaxonomiesListView) },
          { path: 'taxonomies/create', element: lazyElement(TaxonomyEditView) },
          { path: 'taxonomies/:slug', element: lazyElement(TaxonomyDetailView) },
          { path: 'taxonomies/:slug/edit', element: lazyElement(TaxonomyEditView) },
          { path: 'taxonomies/:slug/nodes/new', element: lazyElement(TaxonomyNodeEditView) },
          { path: 'taxonomies/:slug/nodes/:nodeId/edit', element: lazyElement(TaxonomyNodeEditView) },
          { path: 'taxonomies/:slug/import', element: lazyElement(TaxonomyImportView) },

          // Categories
          { path: 'categories', element: lazyElement(CategoriesListView) },
          { path: 'categories/create', element: lazyElement(CategoryCreateView) },
          { path: 'categories/:id/edit', element: lazyElement(CategoryEditView) },

          // Materials
          { path: 'materials', element: lazyElement(MaterialsListView) },
          { path: 'materials/create', element: lazyElement(MaterialCreateView) },
          { path: 'materials/:id/edit', element: lazyElement(MaterialEditView) },

          // Finishes & Laminations — CON-MAT2-003
          { path: 'finishes', element: lazyElement(FinishesListView) },
          { path: 'finishes/create', element: lazyElement(FinishCreateView) },
          { path: 'finishes/:id/edit', element: lazyElement(FinishEditView) },

          // Shapes — CON-SHAPE-001 (list); CON-SHAPE-002 (create); CON-SHAPE-126 (edit).
          { path: 'shapes', element: lazyElement(ShapeListView) },
          { path: 'shapes/create', element: lazyElement(ShapeCreateView) },
          { path: 'shapes/:id/edit', element: lazyElement(ShapeEditView) },

          // Product Types — CON-MAT2-004 / CON-MAT2-005
          { path: 'product-types', element: lazyElement(ProductTypeListView) },
          { path: 'product-types/create', element: lazyElement(ProductTypeCreateView) },
          { path: 'product-types/:id/edit', element: lazyElement(ProductTypeEditView) },

          // Vehicles — CON-VEH-001
          { path: 'vehicles', element: lazyElement(AdminVehicleListPage) },
          { path: 'vehicles/new', element: lazyElement(AdminVehicleEditPage) },
          { path: 'vehicles/:id', element: lazyElement(AdminVehicleEditPage) },
          // CON-LAUNCH-001 — real NHTSA vehicle sync page.
          { path: 'vehicles/sync', element: lazyElement(AdminVehicleSyncPage) },
          // CON-LAUNCH-004 — Wikipedia dimensions hydration page.
          { path: 'vehicles/dimensions', element: lazyElement(AdminDimensionsHydrationPage) },
          // CON-LAUNCH-005 — Vehicle dimensions curation queue.
          { path: 'vehicles/curation', element: lazyElement(AdminVehicleCurationPage) },
          { path: 'vehicles/pvo-import', element: lazyElement(VehiclePvoImportView) },
          { path: 'vehicles/mapping', element: lazyElement(VehicleMappingView) },
          // Backwards-compat: `/vehicles/:id/edit` redirected to the new
          // canonical `/vehicles/:id` form via the same edit page.
          { path: 'vehicles/:id/edit', element: lazyElement(AdminVehicleEditPage) },

          // Orders
          { path: 'orders', element: lazyElement(OrdersListView) },
          // Must precede :id, like import-tracking below, or it is captured.
          {
            path: 'orders/outstanding-balances',
            element: lazyElement(OutstandingBalancesView),
          },
          // CON-WRAP-108 — must precede :id or it is captured.
          { path: 'orders/new', element: lazyElement(StaffWrapOrderCreateView) },
          // CON-SHIPPS-002 — must be before :id to avoid capture.
          // Additional role guard: only admin/superuser may import tracking.
          {
            path: 'orders/import-tracking',
            element: (
              <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
                {lazyElement(ImportTrackingView)}
              </RequireRole>
            ),
          },
          { path: 'orders/:id', element: lazyElement(OrderDetailView) },
          // CON-FULFILLMENT-006 smoke surface — remove with CON-ORDERS-*.

          // Disputes (CON-PAYMENTS-004)
          { path: 'disputes', element: lazyElement(DisputesListView) },
          { path: 'disputes/:id', element: lazyElement(DisputeDetailView) },

          // Quotes
          { path: 'quotes', element: lazyElement(QuotesListView) },
          { path: 'quotes/:id', element: lazyElement(QuoteDetailView) },

          // Custom Design Requests — CON-CDR-001 (list); CON-DSVC-101 (detail page)
          { path: 'custom-design-requests', element: lazyElement(CustomDesignRequestsListView) },
          {
            path: 'custom-design-requests/:id',
            element: lazyElement(CustomDesignRequestDetailView),
          },

          // Prepress
          { path: 'prepress', element: lazyElement(PrepressQueueView) },
          { path: 'prepress/:orderId/artwork', element: lazyElement(PrepressArtworkView) },

          // Production — CON-FULFILLMENT-002 wires the real dashboard view.
          { path: 'production', element: lazyElement(ProductionDashboardView) },
          { path: 'production/queue', element: lazyElement(ProductionQueueView) },
          { path: 'production/shipping', element: lazyElement(ShippingStationView) },
          // CON-FULFILLMENT-004
          { path: 'production/jobs/:id', element: lazyElement(ProductionJobDetailView) },

          // `Operations` is a nav group, not a page — the sidebar renders it as
          // a clickable parent, so without this it fell through to the
          // forbidden route and looked like a permissions failure.
          { path: 'operations', element: <Navigate to="/admin/damage-claims" replace /> },

          // Damage Claims (CON-FULFILLMENT-005)
          { path: 'damage-claims', element: lazyElement(DamageClaimsListView) },
          { path: 'damage-claims/:id', element: lazyElement(DamageClaimDetailView) },

          // Customers
          { path: 'customers', element: lazyElement(CustomersListView) },
          { path: 'customers/:id', element: lazyElement(CustomerDetailView) },

          // Coupons — CON-PROMO-001 (list) / CON-PROMO-002 (create + edit) / CON-PROMO-004 (detail).
          { path: 'coupons', element: lazyElement(CouponsListView) },
          { path: 'coupons/new', element: lazyElement(CouponCreateView) },
          { path: 'coupons/:id', element: lazyElement(CouponDetailPage) },
          { path: 'coupons/:id/edit', element: lazyElement(CouponEditView) },

          // Promotions
          { path: 'promotions/automatic', element: lazyElement(AutomaticPromotionsView) },
          { path: 'promotions/sales', element: lazyElement(SalesView) },
          // Specific paths before :id to avoid capture
          { path: 'promotions/new', element: lazyElement(PromotionFormPage) },
          { path: 'promotions/:id/edit', element: lazyElement(PromotionFormPage) },
          // CON-PROMO-005 — list page at /admin/promotions
          { path: 'promotions', element: lazyElement(PromotionListPage) },

          // SEO — CON-SEO-003: manage-seo permission gate enforced in AdminLayout.
          { path: 'seo/redirects', element: lazyElement(RedirectsListView) },

          // CMS — CON-CMS-001/002 require admin/superuser only.
          {
            path: 'cms/pages',
            element: (
              <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
                {lazyElement(PagesListView)}
              </RequireRole>
            ),
          },
          {
            path: 'cms/pages/create',
            element: (
              <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
                {lazyElement(PageEditorView)}
              </RequireRole>
            ),
          },
          {
            path: 'cms/pages/:id/edit',
            element: (
              <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
                {lazyElement(PageEditorView)}
              </RequireRole>
            ),
          },
          // CON-CMS-003 — FAQ manager, admin/superuser only.
          {
            path: 'cms/faq',
            element: (
              <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
                {lazyElement(FaqManagerView)}
              </RequireRole>
            ),
          },
          // CON-CMS-004 — Gallery manager, admin/superuser only.
          {
            path: 'cms/gallery',
            element: (
              <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
                {lazyElement(GalleryView)}
              </RequireRole>
            ),
          },
          // CON-CMS-005 — Testimonials manager, admin/superuser only.
          {
            path: 'cms/testimonials',
            element: (
              <RequireRole roles={[...STAFF_ROLES]} fallbackPath={adminRoutePaths.forbidden}>
                {lazyElement(TestimonialsManagerView)}
              </RequireRole>
            ),
          },

          // Reports
          { path: 'reports/sales', element: lazyElement(SalesReportView) },
          { path: 'reports/products', element: lazyElement(ProductsReportView) },
          { path: 'reports/production', element: lazyElement(ProductionReportView) },

          // Settings
          { path: 'settings/general', element: lazyElement(GeneralSettingsView) },
          { path: 'settings/navigation', element: lazyElement(NavigationSettingsView) },
          { path: 'settings/quotes', element: lazyElement(QuotesSettingsView) },
          { path: 'settings/test-orders', element: lazyElement(TestOrdersSettingsView) },
          { path: 'settings/homepage', element: lazyElement(HomepageSettingsView) },
          { path: 'settings/shipping', element: lazyElement(ShippingSettingsView) },
          { path: 'settings/tax', element: lazyElement(TaxSettingsView) },
          { path: 'settings/emails', element: lazyElement(EmailSettingsView) },
          { path: 'settings/users', element: lazyElement(UsersSettingsView) },
          { path: 'settings/api', element: lazyElement(ApiSettingsView) },
          { path: 'settings/stripe', element: lazyElement(StripeSettingsView) },
          { path: 'settings/audit-log', element: lazyElement(AuditLogView) },
          { path: 'settings/artwork', element: lazyElement(ArtworkSettingsView) },
        ],
      },
    ],
  },
  // Catch-all — unknown paths fall through to /403 so no route 404s silently.
  {
    path: '*',
    element: <Navigate to={adminRoutePaths.forbidden} replace />,
  },
]);

/**
 * Tiny convenience wrapper so `App.tsx` stays free of router-plumbing noise.
 * This file exports ONLY a component so `react-refresh/only-export-components`
 * stays happy — consumers that need the route-path helpers import them from
 * `@router/paths` directly.
 */
export function AdminRouterProvider() {
  return <RouterProvider router={router} />;
}
